import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Download, Link as LinkIcon, Video, Check, Captions } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const videoFileUrl = (v, download = false) => `${API}/media/videos/${v.id}/file${download ? '?download=1' : ''}`;
export const captionsUrl = (v) => `${API}/media/videos/${v.id}/captions.vtt`;
export const videoShareUrl = (v) => `${window.location.origin}/videos/${v.slug}`;
export const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
export const trackVideo = (v, type) => axios.post(`${API}/media/videos/${v.id}/event`, { type }).catch(() => {});

export const copyShareLink = async (v) => {
  try { await navigator.clipboard.writeText(videoShareUrl(v)); toast.success('Share link copied'); trackVideo(v, 'share'); } catch { toast.error('Could not copy'); }
};

// React doesn't reliably attach <track> children; append it imperatively so captions load.
export const useCaptionsTrack = (ref, v) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !v?.has_captions) return;
    el.querySelectorAll('track').forEach((t) => t.remove());
    const t = document.createElement('track');
    t.kind = 'captions'; t.srclang = 'en'; t.label = 'English'; t.default = true; t.src = captionsUrl(v);
    el.appendChild(t);
    return () => t.remove();
  }, [ref, v]);
};

// <video> that counts the first play and carries the captions track when one exists.
export const TrackedVideo = ({ v, className, ...rest }) => {
  const ref = useRef();
  useCaptionsTrack(ref, v);
  return (
    <video
      ref={ref} controls playsInline preload="metadata" crossOrigin="anonymous"
      poster={v.poster || undefined} src={videoFileUrl(v)} className={className}
      onPlay={(e) => { if (!e.currentTarget.dataset.counted) { e.currentTarget.dataset.counted = '1'; trackVideo(v, 'play'); } }}
      {...rest}
    />
  );
};

export const VideoCard = ({ v, children }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => { await copyShareLink(v); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid={`video-card-${v.slug}`}>
      <TrackedVideo v={v} className="w-full aspect-video bg-black" data-testid={`video-player-${v.slug}`} />
      <div className="p-4 space-y-3">
        <div>
          <Link to={`/videos/${v.slug}`} className="font-semibold text-slate-900 hover:text-[#0F5F5A]" data-testid={`video-title-${v.slug}`}>{v.title}</Link>
          {v.description && <p className="text-sm text-slate-600 mt-0.5">{v.description}</p>}
          <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1">MP4 · {fmtSize(v.size)}{v.has_captions && <><span>·</span><Captions className="w-3.5 h-3.5" /> captions</>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F5F5A] text-white text-sm font-semibold hover:bg-[#0c4c48]" data-testid={`video-copy-link-${v.slug}`}>
            {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}{copied ? 'Copied' : 'Copy share link'}
          </button>
          <a href={videoFileUrl(v, true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid={`video-download-${v.slug}`}>
            <Download className="w-4 h-4" />Download MP4
          </a>
          {children}
        </div>
      </div>
    </div>
  );
};

export const MediaVideosSection = () => {
  const [videos, setVideos] = useState(null);
  useEffect(() => { axios.get(`${API}/media/videos`).then((r) => setVideos(r.data)).catch(() => setVideos([])); }, []);
  if (!videos || videos.length === 0) return null;
  return (
    <section data-testid="media-kit-section-videos">
      <h2 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2 mb-4"><Video className="w-5 h-5 text-[#0F5F5A]" />Promo videos</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {videos.map((v) => <VideoCard key={v.id} v={v} />)}
      </div>
    </section>
  );
};

export default MediaVideosSection;
